let nextLocalId = 0;
const copy = value => JSON.parse(JSON.stringify(value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const hasPendingChanges = pending =>
  pending.created.size > 0 || pending.updated.size > 0 || pending.deleted.size > 0;

// Handsontable replaces row objects when cells change. Keep a stable identity
// for unsaved rows so successive edits do not create duplicate server rows.
export function queueCreatedRow(created, row) {
  if (!row._auditLocalId) row._auditLocalId = `local-${Date.now()}-${++nextLocalId}`;
  for (const previous of created) {
    if (previous._auditLocalId === row._auditLocalId) created.delete(previous);
  }
  created.add(row);
}

export function snapshotChanges(pending) {
  return {
    created: Array.from(pending.created, copy),
    updated: Array.from(pending.updated.values(), copy),
    deleted: Array.from(pending.deleted),
  };
}

export function acknowledgeChanges(pending, sent, response, liveRows) {
  if (!response || response === true || response.created_ids?.length !== sent.created.length) {
    throw new Error('The server did not confirm all saved rows. Your edits are still pending.');
  }
  const remaining = {
    created: new Set(pending.created),
    updated: new Map(pending.updated),
    deleted: new Set(pending.deleted),
  };
  sent.created.forEach((row, index) => {
    const id = response.created_ids[index];
    const queued = Array.from(remaining.created).find(r => r._auditLocalId === row._auditLocalId);
    const live = liveRows.find(r => r._auditLocalId === row._auditLocalId);
    if (queued) remaining.created.delete(queued);
    if (!live) {
      // The user removed this new row while its create request was running.
      remaining.deleted.add(id);
      return;
    }
    const changed = !same(live, row);
    live.id = id;
    if (changed) remaining.updated.set(id, live);
  });
  sent.updated.forEach(row => {
    // Clear only the acknowledged content; a newer edit to the same row stays.
    if (same(remaining.updated.get(row.id), row)) remaining.updated.delete(row.id);
  });
  sent.deleted.forEach(id => remaining.deleted.delete(id));
  return remaining;
}

// APIService leaves status unset for a network failure. Validation/version
// conflicts must not be retried with a silently refreshed version.
export const isRetryableSheetSaveError = error =>
  error?.cause?.retry !== false && (
    error?.status === 0 || error?.status === 409 || error?.status >= 500 ||
    (error?.name === 'API_ERROR' && error?.status == null)
  );
